import React from 'react';

const DirectorModal = ({ director, isOpen, onClose }) => {
  const [language, setLanguage] = React.useState(
    localStorage.getItem('language') || 'en'
  );
  const [theme, setTheme] = React.useState(
    document.documentElement.getAttribute('data-theme') || 'light'
  );

  React.useEffect(() => {
    const handleLanguageChange = (event) => {
      setLanguage(event.detail.language);
    };
    window.addEventListener('languageChange', handleLanguageChange);
    return () => window.removeEventListener('languageChange', handleLanguageChange);
  }, []);

  React.useEffect(() => {
    const root = document.documentElement;
    const updateTheme = () => setTheme(root.getAttribute('data-theme') || 'light');
    updateTheme();

    const observer = new MutationObserver(updateTheme);
    observer.observe(root, {
      attributes: true,
      attributeFilter: ['data-theme']
    });

    return () => observer.disconnect();
  }, []);

  React.useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  const t = (key) => {
    const translations = window.translations || {};
    return translations[language]?.[key] || key;
  };

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  if (!isOpen || !director) return null;

  const isDark = theme === 'dark';
  const modalVars = {
    '--director-modal-surface': isDark ? '#0f172a' : '#ffffff',
    '--director-modal-surface-alt': isDark ? '#1e293b' : '#f8fafc',
    '--director-modal-border': isDark ? 'rgba(148, 163, 184, 0.28)' : 'rgba(15, 23, 42, 0.14)',
    '--director-modal-text': isDark ? '#e2e8f0' : '#0f172a',
    '--director-modal-text-muted': isDark ? '#cbd5e1' : '#475569',
    '--director-modal-shadow': isDark
      ? '0 25px 60px -18px rgba(0, 0, 0, 0.65)'
      : '0 25px 60px -18px rgba(15, 23, 42, 0.28)'
  };

  return (
    <div 
      className="director-modal-backdrop"
      onClick={handleBackdropClick}
      style={{
        ...modalVars,
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: isDark ? 'rgba(2, 6, 23, 0.78)' : 'rgba(15, 23, 42, 0.55)',
        zIndex: 10000,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '2rem',
        animation: 'fadeIn 0.3s ease-out'
      }}
    >
      <style>
        {`
          @keyframes fadeIn {
            from { opacity: 0; }
            to { opacity: 1; }
          }
          @keyframes slideUp {
            from { 
              opacity: 0;
              transform: translateY(30px);
            }
            to { 
              opacity: 1;
              transform: translateY(0);
            }
          }
          .director-modal-content {
            background: var(--director-modal-surface);
            border-radius: 24px;
            max-width: 900px;
            width: 100%;
            max-height: 90vh;
            overflow-y: auto;
            position: relative;
            box-shadow: var(--director-modal-shadow);
            animation: slideUp 0.4s ease-out;
            backdrop-filter: blur(10px);
            border: 1px solid var(--director-modal-border);
          }
          .director-modal-close {
            position: absolute;
            top: 1.5rem;
            right: 1.5rem;
            background: var(--director-modal-surface-alt);
            border: none;
            width: 44px;
            height: 44px;
            border-radius: 50%;
            cursor: pointer;
            display: flex;
            align-items: center;
            justify-content: center;
            transition: all 0.3s ease;
            color: var(--director-modal-text);
            font-size: 24px;
            z-index: 10;
            border: 1px solid var(--director-modal-border);
          }
          .director-modal-close:hover {
            background: var(--director-modal-surface-alt);
            opacity: 0.8;
            transform: rotate(90deg);
          }
          .director-modal-grid {
            display: grid;
            grid-template-columns: 300px 1fr;
            gap: 3rem;
            padding: 3rem;
          }
          .director-modal-image {
            width: 100%;
            height: 400px;
            object-fit: cover;
            border-radius: 16px;
            border: 2px solid var(--director-modal-border);
          }
          .director-modal-info {
            color: var(--director-modal-text);
          }
          .director-modal-name {
            font-size: 2.5rem;
            font-weight: 700;
            margin-bottom: 0.5rem;
            color: var(--director-modal-text);
          }
          .director-modal-title {
            font-size: 1.125rem;
            color: var(--director-modal-text-muted);
            margin-bottom: 2rem;
            font-style: italic;
          }
          .director-modal-bio {
            font-size: 1rem;
            line-height: 1.8;
            color: var(--director-modal-text);
            margin-bottom: 2rem;
          }
          .director-modal-contacts {
            display: flex;
            gap: 1rem;
            flex-wrap: wrap;
          }
          .director-contact-link {
            display: inline-flex;
            align-items: center;
            gap: 0.5rem;
            padding: 0.75rem 1.5rem;
            background: var(--director-modal-surface-alt);
            border-radius: 12px;
            color: var(--director-modal-text);
            text-decoration: none;
            transition: all 0.3s ease;
            border: 1px solid var(--director-modal-border);
          }
          .director-contact-link:hover {
            background: var(--director-modal-surface-alt);
            opacity: 0.8;
            transform: translateY(-2px);
            box-shadow: 0 4px 12px rgba(15, 23, 42, 0.2);
          }
          .director-contact-icon {
            font-size: 1.25rem;
          }
          @media (max-width: 768px) {
            .director-modal-grid {
              grid-template-columns: 1fr;
              gap: 2rem;
              padding: 2rem;
            }
            .director-modal-image {
              height: 300px;
            }
            .director-modal-name {
              font-size: 2rem;
            }
            .director-modal-close {
              top: 1rem;
              right: 1rem;
            }
          }
        `}
      </style>
      <div className="director-modal-content">
        <button 
          className="director-modal-close"
          onClick={onClose}
          aria-label={t('Close')}
        >
          ×
        </button>
        <div className="director-modal-grid">
          <div>
            <img 
              src={director.image} 
              alt={director.name}
              className="director-modal-image"
            />
          </div>
          <div className="director-modal-info">
            <h2 className="director-modal-name">{director.name}</h2>
            <p className="director-modal-title">{t(director.titleKey)}</p>
            <div className="director-modal-bio">
              {(Array.isArray(director.bioParagraphs) && director.bioParagraphs.length > 0
                ? director.bioParagraphs
                : director.bioKey.split('\n').map((paragraph) => t(paragraph))
              ).map((paragraph, index) => (
                <p key={index} style={{ marginBottom: '1rem' }}>
                  {paragraph}
                </p>
              ))}
            </div>
            <div className="director-modal-contacts">
              {director.linkedin && (
                <a 
                  href={director.linkedin}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="director-contact-link"
                >
                  <span className="director-contact-icon">💼</span>
                  {t('LinkedIn')}
                </a>
              )}
              {director.email && (
                <a 
                  href={`mailto:${director.email}`}
                  className="director-contact-link"
                >
                  <span className="director-contact-icon">✉️</span>
                  {t('Email')}
                </a>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DirectorModal;
